// 🆕 Feb 2026 — WebAuthn / Passkey helpers per Area Cliente
// Il metodo esistente (magic-link + credenziali) resta invariato come fallback.
import {
  startAuthentication,
  startRegistration,
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
} from "@simplewebauthn/browser";
import { api } from "./api";

export const isPasskeySupported = () => {
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
};

/** True se il browser supporta il conditional UI (autofill delle passkey nel campo email). */
export const isPasskeyAutofillSupported = async () => {
  try {
    return await browserSupportsWebAuthnAutofill();
  } catch {
    return false;
  }
};

/** Registra una nuova passkey per il cliente autenticato.
 *  Il chiamante deve gestire eventuali toast/UI. */
export async function registerPasskey(passkeyName) {
  const start = await api.post("/client/passkeys/registration/start");
  const { challenge_id, options } = start.data;
  let credential;
  try {
    credential = await startRegistration({ optionsJSON: options });
  } catch (err) {
    throw _translatePasskeyError(err);
  }
  const finish = await api.post("/client/passkeys/registration/finish", {
    challenge_id,
    credential,
    passkey_name: passkeyName || null,
  });
  return finish.data;
}

/** Autenticazione biometrica: identifica il cliente via email, esegue WebAuthn
 *  e riceve access_token identico a quello del magic-link. */
export async function loginWithPasskey(email) {
  const start = await api.post("/public/client-portal/passkeys/authentication/start", { email });
  const { challenge_id, options } = start.data;
  let credential;
  try {
    credential = await startAuthentication({ optionsJSON: options });
  } catch (err) {
    throw _translatePasskeyError(err);
  }
  const finish = await api.post("/public/client-portal/passkeys/authentication/finish", {
    challenge_id,
    credential,
  });
  return finish.data;
}

/** 🆕 Feb 2026 — Conditional UI (autofill): l'utente non digita nulla, il browser
 *  suggerisce le passkey disponibili al focus del campo email.
 *  Restituisce una Promise che si risolve quando l'utente seleziona una passkey.
 *  L'AbortSignal è opzionale ma consigliato per cancellare quando il componente si smonta. */
export async function loginWithPasskeyAutofill({ signal } = {}) {
  const start = await api.post("/public/client-portal/passkeys/conditional/start");
  const { challenge_id, options } = start.data;
  let credential;
  try {
    credential = await startAuthentication({
      optionsJSON: options,
      useBrowserAutofill: true,
      // NOTA: startAuthentication non accetta direttamente un signal; l'abort avviene
      // automaticamente quando l'utente naviga via o quando il componente si smonta.
    });
  } catch (err) {
    // NotAllowedError su autofill significa spesso "utente non ha scelto nulla"
    // → non è un errore utente, silenzialo.
    if (err?.name === "NotAllowedError") {
      const e = new Error("autofill_cancelled");
      e.silent = true;
      throw e;
    }
    throw _translatePasskeyError(err);
  }
  if (signal?.aborted) {
    const e = new Error("autofill_cancelled");
    e.silent = true;
    throw e;
  }
  const finish = await api.post("/public/client-portal/passkeys/authentication/finish", {
    challenge_id,
    credential,
  });
  return finish.data;
}

export async function listPasskeys() {
  const { data } = await api.get("/client/passkeys");
  return data;
}

export async function renamePasskey(passkeyId, name) {
  const { data } = await api.patch(`/client/passkeys/${passkeyId}`, { name });
  return data;
}

export async function deletePasskey(passkeyId) {
  const { data } = await api.delete(`/client/passkeys/${passkeyId}`);
  return data;
}

// Traduzione errori WebAuthn (nomi standard DOMException + custom axios)
function _translatePasskeyError(err) {
  const name = err?.name || "";
  if (name === "NotAllowedError") {
    return new Error("Operazione annullata o timeout scaduto. Riprova o usa il metodo alternativo.");
  }
  if (name === "InvalidStateError") {
    return new Error("Questo dispositivo risulta già registrato per questa area cliente.");
  }
  if (name === "NotSupportedError") {
    return new Error("Il tuo dispositivo o browser non supporta l'accesso biometrico.");
  }
  if (name === "SecurityError") {
    return new Error("Errore di sicurezza: apri l'app in HTTPS sul dominio corretto.");
  }
  return new Error(err?.message || "Errore imprevisto durante l'operazione biometrica.");
}
